// Plan de capacidad del día: demanda de los batches vs dotación (mano de obra)
// y equipos por estación → cuello de botella, hora pico y recomendación, ANTES
// de arrancar la producción. Catering → Piso de cocina → Plan de capacidad.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Cog, Factory, Gauge, Loader2, Save, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { fieldCls, num } from '@/features/inventario/shared';
import { catRpc, hhmm, invalidateCatering, SEMAFORO_CLS, SEMAFORO_TXT } from './shared';

const hoyLocal = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const h1 = (min: number): string => num(min / 60, 1); // minutos → horas con 1 decimal

interface Linea {
  estacion_id: string | null; estacion: string; color: string | null;
  batches: number; porciones: number;
  personas: number; min_persona_dia: number;
  demanda_mo_min: number; capacidad_mo_min: number; util_mo_pct: number | null;
  pico_mo: number | null; hora_pico: string | null; personas_faltan: number;
  equipo_pico: string | null; equipo_unidades: number | null;
  equipo_pico_uso: number | null; util_equipo_pct: number | null;
  semaforo: 'verde' | 'ambar' | 'rojo'; cuello: boolean; recomendacion: string;
}
interface Plan {
  fecha: string; tiene_plan: boolean; estaciones: Linea[];
  resumen: {
    batches: number; porciones: number; estaciones: number; sin_estacion: number;
    mo_min_req: number; mo_min_disp: number; util_global_pct: number | null;
    rojo: number; ambar: number; verde: number;
    cuello_estacion: string | null; cuello_util_pct: number | null; cuello_hora: string | null;
  };
}
interface EstConfig {
  estacion_id: string; estacion: string; color: string | null; orden: number;
  personas: number; min_persona_dia: number; override_dia: number | null; override_nota: string | null;
}

const SEM_LABEL: Record<string, string> = { verde: 'Holgado', ambar: 'Ajustado', rojo: 'Cuello' };
const pct = (v: number | null): string => (v == null ? '—' : `${num(v, 0)} %`);

export function CapacidadCatering() {
  const qc = useQueryClient();
  const [fecha, setFecha] = useState(hoyLocal());
  const [config, setConfig] = useState(false);

  const { data: plan, isLoading } = useQuery<Plan>({
    queryKey: ['cat-capacidad', fecha],
    queryFn: () => catRpc<Plan>('cat_capacidad_dia', { _fecha: fecha }),
  });
  const { data: ests } = useQuery<EstConfig[]>({
    queryKey: ['cat-capacidad-est', fecha],
    queryFn: () => catRpc<EstConfig[]>('cat_capacidad_estaciones', { _fecha: fecha }),
  });

  const setOverride = useMutation({
    mutationFn: (p: { estacion_id: string; personas: number | null; nota?: string }) =>
      catRpc('cat_capacidad_dia_set', { _fecha: fecha, _estacion_id: p.estacion_id, _personas: p.personas, _nota: p.nota ?? null }),
    onSuccess: () => { invalidateCatering(qc); toast.success('Dotación del día actualizada'); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const setBaseline = useMutation({
    mutationFn: (p: { estacion_id: string; personas: number; min: number }) =>
      catRpc('cat_capacidad_estacion_set', { _estacion_id: p.estacion_id, _personas: p.personas, _min_persona_dia: p.min }),
    onSuccess: () => { invalidateCatering(qc); toast.success('Dotación base guardada'); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const r = plan?.resumen;

  return (
    <div className="space-y-4 p-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Gauge className="h-4 w-4" /> Plan de capacidad del día
          </CardTitle>
          <div className="flex flex-wrap items-end gap-2 pt-1">
            <div>
              <label className="block text-xs text-muted-foreground">Fecha</label>
              <input type="date" className={`${fieldCls} w-44`} value={fecha} onChange={(e) => setFecha(e.target.value)} />
            </div>
            <Button variant="outline" size="sm" onClick={() => setConfig((c) => !c)} className="gap-1">
              <Cog className="h-4 w-4" /> Dotación base
            </Button>
            <span className="pb-1 text-xs text-muted-foreground">
              Cruza los batches del día (Piso de cocina) contra la dotación y los equipos por estación.
            </span>
          </div>
        </CardHeader>

        <CardContent>
          {isLoading && <p className="py-6 text-sm text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin" /> Calculando…</p>}

          {!isLoading && plan && !plan.tiene_plan && (
            <div className="flex items-start gap-2 rounded-md border border-amber-400 bg-amber-50/60 p-3 text-sm dark:bg-amber-900/10">
              <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-600" />
              <div>
                <p className="font-medium">No hay plan de producción para este día.</p>
                <p className="text-muted-foreground">Genera los batches desde la orden de producción (Piso de cocina) y vuelve a este plan para evaluar la capacidad.</p>
              </div>
            </div>
          )}

          {!isLoading && plan?.tiene_plan && r && (
            <>
              {/* Resumen del día */}
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <div className="rounded-md border p-2">
                  <p className="text-[11px] uppercase text-muted-foreground">Utilización global MO</p>
                  <p className="text-xl font-semibold">{pct(r.util_global_pct)}</p>
                  <p className="text-[11px] text-muted-foreground">{h1(r.mo_min_req)} h de {h1(r.mo_min_disp)} h</p>
                </div>
                <div className={`rounded-md border-2 p-2 ${r.rojo > 0 ? SEMAFORO_CLS.rojo : r.ambar > 0 ? SEMAFORO_CLS.ambar : SEMAFORO_CLS.verde}`}>
                  <p className="text-[11px] uppercase text-muted-foreground">Cuello de botella</p>
                  <p className="truncate text-base font-semibold" title={r.cuello_estacion ?? ''}>{r.cuello_estacion ?? '—'}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {r.cuello_util_pct != null ? `${num(r.cuello_util_pct, 0)}% MO` : '—'}
                    {r.cuello_hora ? ` · pico ~${hhmm(r.cuello_hora)}` : ''}
                  </p>
                </div>
                <div className="rounded-md border p-2">
                  <p className="text-[11px] uppercase text-muted-foreground">Estaciones</p>
                  <p className="text-xl font-semibold">{r.estaciones}</p>
                  <p className="flex gap-2 text-[11px]">
                    <span className={SEMAFORO_TXT.rojo}>● {r.rojo}</span>
                    <span className={SEMAFORO_TXT.ambar}>● {r.ambar}</span>
                    <span className={SEMAFORO_TXT.verde}>● {r.verde}</span>
                  </p>
                </div>
                <div className="rounded-md border p-2">
                  <p className="text-[11px] uppercase text-muted-foreground">Producción</p>
                  <p className="text-xl font-semibold">{num(r.batches, 0)}<span className="text-sm font-normal text-muted-foreground"> batches</span></p>
                  <p className="text-[11px] text-muted-foreground">{num(r.porciones, 0)} porciones</p>
                </div>
              </div>

              {r.sin_estacion > 0 && (
                <div className="mt-2 flex items-center gap-2 rounded-md border border-red-400 bg-red-50/60 p-2 text-xs dark:bg-red-900/10">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  {num(r.sin_estacion, 0)} batch(es) sin estación asignada — no entran al balance. Asigna estación/equipo a esas recetas.
                </div>
              )}

              {/* Tabla por estación */}
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs text-muted-foreground">
                      <th className="py-1 pr-2">#</th>
                      <th className="py-1 pr-2">Estación</th>
                      <th className="py-1 pr-2 text-right">Batches</th>
                      <th className="py-1 pr-2 text-right">Dotación</th>
                      <th className="py-1 pr-2 text-right">Carga MO</th>
                      <th className="py-1 pr-2 text-right">Util MO</th>
                      <th className="py-1 pr-2 text-right">Pico (a la vez)</th>
                      <th className="py-1 pr-2">Equipo crítico</th>
                      <th className="py-1 pr-2">Estado</th>
                      <th className="py-1 pr-2">Recomendación</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.estaciones.map((l, i) => (
                      <tr key={l.estacion_id ?? 'sin'} className="border-b align-top last:border-0">
                        <td className="py-1.5 pr-2 text-muted-foreground">{i + 1}</td>
                        <td className="py-1.5 pr-2 font-medium">
                          <span className="inline-block h-2 w-2 rounded-full align-middle" style={{ background: l.color ?? '#94a3b8' }} /> {l.estacion}
                        </td>
                        <td className="py-1.5 pr-2 text-right font-mono">{num(l.batches, 0)}<span className="block text-[11px] text-muted-foreground">{num(l.porciones, 0)} porc</span></td>
                        <td className="py-1.5 pr-2 text-right">
                          {l.estacion_id ? (
                            <OverrideInput
                              value={l.personas}
                              disabled={setOverride.isPending}
                              onSave={(v) => setOverride.mutate({ estacion_id: l.estacion_id as string, personas: v })}
                            />
                          ) : '—'}
                        </td>
                        <td className="py-1.5 pr-2 text-right font-mono">{h1(l.demanda_mo_min)} / {h1(l.capacidad_mo_min)} h</td>
                        <td className={`py-1.5 pr-2 text-right font-mono ${l.util_mo_pct != null && l.util_mo_pct > 100 ? SEMAFORO_TXT.rojo : ''}`}>{pct(l.util_mo_pct)}</td>
                        <td className="py-1.5 pr-2 text-right font-mono">
                          <span className={l.personas_faltan > 0 ? SEMAFORO_TXT.rojo : ''}>{l.pico_mo ?? '—'}</span>
                          <span className="text-muted-foreground"> / {num(l.personas, 0)}</span>
                          <span className="block text-[11px] text-muted-foreground">{l.hora_pico ? `~${hhmm(l.hora_pico)}` : ''}</span>
                        </td>
                        <td className="py-1.5 pr-2 text-xs">
                          {l.equipo_pico ? (
                            <>
                              <span className="flex items-center gap-1"><Factory className="h-3 w-3" /> {l.equipo_pico}</span>
                              <span className={`text-[11px] ${l.util_equipo_pct != null && l.util_equipo_pct > 100 ? SEMAFORO_TXT.rojo : 'text-muted-foreground'}`}>
                                {l.equipo_pico_uso}/{l.equipo_unidades} uds · {pct(l.util_equipo_pct)}
                              </span>
                            </>
                          ) : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="py-1.5 pr-2">
                          <span className={`inline-block rounded-full border-2 px-2 py-0.5 text-[11px] font-semibold ${SEMAFORO_CLS[l.semaforo]} ${SEMAFORO_TXT[l.semaforo]}`}>
                            {SEM_LABEL[l.semaforo]}
                          </span>
                        </td>
                        <td className="py-1.5 pr-2 text-xs text-muted-foreground">{l.recomendacion}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="pt-2 text-[11px] text-muted-foreground">
                <Users className="mr-1 inline h-3 w-3" />
                <b>Util MO</b> = carga de trabajo del día ÷ jornada disponible. <b>Pico</b> = batches que coinciden a la vez en la estación
                (personas necesarias simultáneamente). El semáforo se pone en rojo por sobrecarga del día <i>o</i> por pico &gt; dotación.
                La dotación es editable para <i>este día</i>; la base se ajusta en «Dotación base».
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {/* Config de dotación base por estación */}
      {config && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm"><Cog className="h-4 w-4" /> Dotación base por estación</CardTitle>
            <p className="text-xs text-muted-foreground">Personas asignadas y minutos productivos por jornada (por defecto 420 = 7 h). Aplica a todos los días salvo que pongas un override puntual arriba.</p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-1 pr-2">Estación</th>
                    <th className="py-1 pr-2 text-right">Personas base</th>
                    <th className="py-1 pr-2 text-right">Min/persona-jornada</th>
                    <th className="py-1 pr-2 text-right">Override hoy</th>
                    <th className="py-1 pr-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {(ests ?? []).map((e) => (
                    <BaselineRow key={e.estacion_id} e={e} saving={setBaseline.isPending}
                      onSave={(personas, min) => setBaseline.mutate({ estacion_id: e.estacion_id, personas, min })} />
                  ))}
                  {!ests?.length && <tr><td colSpan={5} className="py-3 text-sm text-muted-foreground">No hay estaciones. Créalas en «Estaciones y equipos».</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Input de dotación del día (override): número editable con guardar al confirmar
function OverrideInput({ value, onSave, disabled }: { value: number; onSave: (v: number) => void; disabled?: boolean }) {
  const [v, setV] = useState(String(value));
  const changed = Number(v) !== Number(value) && v !== '';
  return (
    <span className="inline-flex items-center gap-1">
      <input
        type="number" min={0} step={0.5} disabled={disabled}
        className={`${fieldCls} w-16 text-right`} value={v}
        onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && changed) onSave(Number(v)); }}
      />
      {changed && (
        <button className="text-emerald-600 hover:text-emerald-800" title="Guardar dotación de hoy" onClick={() => onSave(Number(v))}>
          <Save className="h-4 w-4" />
        </button>
      )}
    </span>
  );
}

function BaselineRow({ e, onSave, saving }: { e: EstConfig; onSave: (personas: number, min: number) => void; saving: boolean }) {
  const [personas, setPersonas] = useState(String(e.personas));
  const [min, setMin] = useState(String(e.min_persona_dia));
  const changed = (Number(personas) !== Number(e.personas) || Number(min) !== Number(e.min_persona_dia)) && personas !== '' && min !== '';
  return (
    <tr className="border-b last:border-0">
      <td className="py-1.5 pr-2 font-medium">
        <span className="inline-block h-2 w-2 rounded-full align-middle" style={{ background: e.color ?? '#94a3b8' }} /> {e.estacion}
      </td>
      <td className="py-1.5 pr-2 text-right">
        <input type="number" min={0} step={0.5} className={`${fieldCls} w-20 text-right`} value={personas} onChange={(ev) => setPersonas(ev.target.value)} />
      </td>
      <td className="py-1.5 pr-2 text-right">
        <input type="number" min={1} step={30} className={`${fieldCls} w-24 text-right`} value={min} onChange={(ev) => setMin(ev.target.value)} />
      </td>
      <td className="py-1.5 pr-2 text-right text-xs text-muted-foreground">{e.override_dia != null ? `${num(e.override_dia, 0)} personas` : '—'}</td>
      <td className="py-1.5 pr-2">
        <Button size="sm" variant={changed ? 'default' : 'outline'} disabled={!changed || saving} className="gap-1"
          onClick={() => onSave(Number(personas), Number(min))}>
          <Save className="h-3.5 w-3.5" /> Guardar
        </Button>
      </td>
    </tr>
  );
}
